import { StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";

import { Chip } from "../../primitives/Chip";
import type { UiTheme } from "../../theme";

export type PrescribeView = "matrice" | "editor";

export type PrescribeViewTabsProps = {
  theme: UiTheme;
  value: PrescribeView;
  onSelect: (view: PrescribeView) => void;
};

// PE-27 — the `Matrice · Editor` switcher that splits the prescribe editor's
// once-vertical stack (card + two matrices + coverage) into two tabs, so the
// full-height card and the placement grids never scroll past each other.
// ⛔ NOT a new SegmentedControl: it reuses the shared `<Chip>` primitive the
// siblings LoadAxisSelector/PeriodSelector already lean on (the consolidation
// scout's call, next/consolidation.md). Unlike those two — which pick a VALUE —
// this SWITCHES which panel is shown, so the a11y role is `tablist`/`tab`, not
// `radiogroup`/`radio`. Selected state stays non-colour-only (leading "✓ " +
// fill). `flex: 1` per chip gives the equal-width segment look; the hitSlop is
// vertical-only because the 8-all-sides default would overlap the 4px gap
// between the two full-width chips (same fix as PeriodSelector.tsx:49).
export function PrescribeViewTabs({
  theme,
  value,
  onSelect,
}: PrescribeViewTabsProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const groupLabel = t("composer.prescribeViewGroup", {
    defaultValue: "Switch view",
  });

  return (
    <View
      accessibilityRole="tablist"
      accessibilityLabel={groupLabel}
      style={styles.row}
    >
      <Chip
        theme={theme}
        role="tab"
        shape="box"
        label={t("composer.prescribeViewMatrix", { defaultValue: "Matrix" })}
        selected={value === "matrice"}
        onPress={() => onSelect("matrice")}
        hitSlop={{ top: 8, bottom: 8 }}
        style={styles.tab}
      />
      <Chip
        theme={theme}
        role="tab"
        shape="box"
        label={t("composer.prescribeViewEditor", { defaultValue: "Editor" })}
        selected={value === "editor"}
        onPress={() => onSelect("editor")}
        hitSlop={{ top: 8, bottom: 8 }}
        style={styles.tab}
      />
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { spacing: s } = theme;
  return StyleSheet.create({
    row: {
      flexDirection: "row",
      gap: s.s2,
      paddingHorizontal: s.s4,
      paddingVertical: s.s2,
    },
    tab: { flex: 1 },
  });
}
